import React, { useEffect, useMemo, useState } from "react";

export default function OrderModal({ product, onClose, inline = false, onAddToCart }) {
  const [quantity, setQuantity] = useState(1);
  const [zoomSrc, setZoomSrc] = useState(null);
  const [currentImage, setCurrentImage] = useState(0);

  useEffect(() => {
    if (product) {
      setQuantity(1);
      setZoomSrc(null);
      setCurrentImage(0);
    }
  }, [product]);

  const variantItems = useMemo(() => product?.variants || [], [product]);
  const hasVariants = variantItems.length > 0;
  const getVariantLabel = (variant) =>
    variant?.label_value || variant?.color || variant?.size || variant?.label || "";
  const carouselItems = useMemo(() => {
    if (variantItems.length) {
      return variantItems.map((variant) => ({
        src: variant.image_url || null,
        label: getVariantLabel(variant)
      }));
    }
    const imgs = product?.images && product.images.length ? product.images : product?.image_url ? [product.image_url] : [];
    return imgs.map((src) => ({ src, label: "" }));
  }, [product, variantItems]);
  const selectedVariant = hasVariants ? variantItems[currentImage] || variantItems[0] : null;

  useEffect(() => {
    if (currentImage >= carouselItems.length) {
      setCurrentImage(0);
    }
  }, [carouselItems.length, currentImage]);

  const detailText = product?.product_details || product?.details || product?.description || "";
  const detailLines = detailText
    ? detailText
        .split(/[\n\r]+|(?<=\.)\s+/)
        .map((s) => s.trim())
        .filter(Boolean)
    : [];

  const unitPrice = useMemo(
    () => product?.selling_cost ?? product?.product_cost ?? 0,
    [product]
  );
  const total = unitPrice * quantity;

  if (!product) return null;

  const shell = (
    <div className="max-w-5xl w-full rounded-3xl border border-white/15 bg-[#0a0b12] text-white shadow-2xl overflow-hidden">
      <div className="flex items-center justify-between px-6 py-4 border-b border-white/10">
        <div>
          <p className="text-xs uppercase tracking-[0.2em] text-amber-200/80">Order</p>
          <h3 className="text-xl font-bold">{product.name}</h3>
        </div>
        <button
          onClick={onClose}
          className="rounded-full bg-white/10 px-3 py-1 text-sm hover:bg-white/20 transition"
        >
          {inline ? "Back" : "Close"}
        </button>
      </div>

      <div className="grid md:grid-cols-5 gap-0">
        <div className="md:col-span-2 p-6 border-b md:border-b-0 md:border-r border-white/10">
          <div className="rounded-2xl overflow-hidden border border-white/10 bg-black/40 relative">
            {carouselItems[currentImage]?.src ? (
              <img
                src={carouselItems[currentImage]?.src}
                alt={product.image_alt || product.name}
                className="w-full object-cover h-56 cursor-zoom-in"
                onClick={() => setZoomSrc(carouselItems[currentImage]?.src)}
              />
            ) : (
              <div className="h-56 flex items-center justify-center text-gray-400 text-sm">
                Image unavailable
              </div>
            )}
          </div>
          {carouselItems.length > 1 ? (
            <div className="flex items-center justify-center gap-2 mt-3">
              {carouselItems.map((item, idx) => (
                <button
                  key={(item?.src || "img") + idx}
                  onClick={() => setCurrentImage(idx)}
                  className={`h-3 w-3 rounded-full border border-white/30 ${currentImage === idx ? "bg-amber-300" : "bg-white/10"}`}
                  aria-label={`View variant ${idx + 1}`}
                />
              ))}
            </div>
          ) : null}
          {hasVariants ? (
            <div className="mt-3">
              <p className="text-xs text-gray-300 text-center">Select a variant</p>
              <div className="mt-2 flex flex-wrap justify-center gap-2">
                {variantItems.map((variant, idx) => {
                  const label = getVariantLabel(variant) || `Variant ${idx + 1}`;
                  const isActive = idx === currentImage;
                  return (
                    <button
                      key={`${variant?.variant_sku || variant?.id || "variant"}-${idx}`}
                      type="button"
                      onClick={() => setCurrentImage(idx)}
                      className={`rounded-full border px-3 py-1 text-xs ${
                        isActive ? "border-amber-300 bg-amber-300/20 text-amber-100" : "border-white/20 text-gray-200"
                      }`}
                    >
                      {label}
                    </button>
                  );
                })}
              </div>
            </div>
          ) : carouselItems[currentImage]?.label ? (
            <div className="mt-3 text-center text-xs text-amber-200">{carouselItems[currentImage]?.label}</div>
          ) : null}
          <div className="mt-4 space-y-2 text-sm text-gray-200">
            <div className="flex justify-between">
              <span>Unit price</span>
              <span className="text-amber-300 font-semibold">₹{unitPrice}</span>
            </div>
            <div className="flex justify-between">
              <span>Quantity</span>
              <div className="inline-flex items-center rounded-full border border-white/15">
                <button
                  type="button"
                  disabled={quantity <= 1}
                  onClick={() => setQuantity((q) => Math.max(1, q - 1))}
                  className="px-3 py-1 text-white/80 disabled:opacity-40"
                >
                  -
                </button>
                <span className="px-3 py-1 font-semibold">{quantity}</span>
                <button
                  type="button"
                  disabled={quantity >= 5}
                  onClick={() => setQuantity((q) => Math.min(5, q + 1))}
                  className="px-3 py-1 text-white/80 disabled:opacity-40"
                >
                  +
                </button>
              </div>
            </div>
            <div className="flex justify-between text-lg font-extrabold">
              <span>Total</span>
              <span className="text-amber-300">₹{total}</span>
            </div>
            {product.store_sku ? (
              <div className="text-xs text-gray-400">
                Store SKU: {product.store_sku} {product.design_sku ? `| Design: ${product.design_sku}` : ""}
              </div>
            ) : null}
            {onAddToCart ? (
              <button
                type="button"
                onClick={() => {
                  onAddToCart({
                    product,
                    quantity,
                    unitPrice,
                    variant: selectedVariant
                      ? {
                          id: selectedVariant.id,
                          variant_sku: selectedVariant.variant_sku,
                          label: selectedVariant.label,
                          color: selectedVariant.color,
                          size: selectedVariant.size,
                          label_value: getVariantLabel(selectedVariant),
                          image_url: selectedVariant.image_url
                        }
                      : null,
                    variant_label: selectedVariant ? getVariantLabel(selectedVariant) : null
                  });
                  onClose();
                }}
                className="w-full mt-4 inline-flex items-center justify-center gap-2 rounded-full bg-white/10 border border-white/15 text-white font-semibold px-4 py-3 hover:bg-white/15 transition"
              >
                Add to Cart
              </button>
            ) : null}
          </div>
        </div>

        <div className="md:col-span-3 p-6 space-y-4">
          <div className="space-y-4 text-sm text-gray-300">
            <div>
              <p className="text-white font-semibold">Add to cart</p>
              <p>Select quantity, then add to cart. Checkout all items together from the cart icon.</p>
            </div>
            {detailLines.length ? (
              <div className="rounded-2xl border border-white/10 bg-white/5 p-4 space-y-2">
                <p className="text-white font-semibold text-base">Product details</p>
                <ul className="space-y-1 list-disc list-inside text-gray-200">
                  {detailLines.map((line, idx) => (
                    <li key={idx} className="leading-relaxed">
                      {line}
                    </li>
                  ))}
                </ul>
              </div>
            ) : null}
          </div>
        </div>
      </div>
    </div>
  );

  const zoomOverlay = zoomSrc ? (
    <div
      className="fixed inset-0 z-50 bg-black/80 flex items-center justify-center px-4"
      onClick={() => setZoomSrc(null)}
    >
      <img src={zoomSrc} alt="Zoomed" className="max-h-[90vh] max-w-[90vw] object-contain rounded-2xl border border-white/10 shadow-2xl" />
    </div>
  ) : null;

  if (inline) {
    return (
      <>
        <div className="mt-6 flex justify-center">{shell}</div>
        {zoomOverlay}
      </>
    );
  }

  return (
    <div className="fixed inset-0 z-40 flex items-center justify-center px-4 py-8 bg-black/70 backdrop-blur-md">
      {shell}
      {zoomOverlay}
    </div>
  );
}
